import CleanTextContextMenu from '@/components/CleanTextContextMenu';
import FilesSidebarResetBoundary from '@/components/FilesSidebarResetBoundary';
import SidebarActionsWrapper from '@/components/RightContent/SidebarActionsWrapper';
import { useCompactSidebar } from '@/hooks/useCompactSidebar';
import {
  localizeGrammarOfUseFrame,
  type CopyMap,
} from '@/pages/AngelInsider/utils/localizeGrammarOfUse';
import { getCurrentUser, getUserMeta, logout } from '@/services/api';
import { getLibrary } from '@/services/library.service';
import { ClipboardIcon, CopyIcon } from '@/utils/icons';
import { isOutlookPath } from '@/utils/outlook';
import { clearAllWsSessions } from '@/utils/wsSession';
import {
  CaretDownFilled,
  CaretUpFilled,
  HolderOutlined,
  MinusOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import {
  MenuDataItem,
  PageContainer,
  PageLoading,
  ProCard,
  Settings as LayoutSettings,
} from '@ant-design/pro-components';
import {
  FormattedMessage,
  getIntl,
  getLocale,
  history,
  Link,
  RunTimeLayoutConfig,
  setLocale,
  useLocation,
} from '@umijs/max';
import {
  Button,
  Card,
  ConfigProvider,
  Input,
  List,
  message,
  Modal,
  Result,
  Tooltip,
  Typography,
} from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import { Colors } from '../config/colors';
import defaultSettings from '../config/defaultSettings';
import BillingRequiredOverlay from './components/BillingRequiredOverlay';
import { DownloadLoadingProvider } from './components/DownloadLoadingMask';
import { FileTreeProvider } from './context/FileTreeContext';
import FileTreeInitializer from './context/FileTreeInitializer';
import IdleForm from './pages/User/IdleForm';
import { errorConfig } from './requestErrorConfig';
import {
  AdminIcon,
  AnalysisIcon,
  ChatIcon,
  ComparisonIcon,
  DiagnosticsIcon,
  FolderIcon,
  GrammarOfUseIcon,
  HomeIcon,
  LibraryIcon,
  LogoutIcon,
  ManagementIcon,
  MenuIcon,
  ProjectsHubIcon,
  QuoteIcon,
  ReviewIcon,
  ScribeIcon,
  SearchIcon,
  SettingIcon,
  SideBySideIcon,
  TeamIcon,
  TemplateIcon,
  TranslationIcon,
  WorkstudioIcon,
} from './utils/helper';
import { AudioIcon, DiffIcon, HeartIcon, ReadIcon } from './utils/icons';

const loginPath = '/user/login';
const forgotPasswordPath = '/user/forgot-password';
const resetPasswordPath = '/user/reset-password';
const updatePasswordPath = '/update-password';
const DEFAULT_SIDEBAR_WIDTH = 200;
const MIN_SIDEBAR_WIDTH = 180;
const MAX_SIDEBAR_WIDTH = 320;
const COMPACT_SIDEBAR_WIDTH = 140;
const COMPACT_MIN_SIDEBAR_WIDTH = 126;
const COMPACT_MAX_SIDEBAR_WIDTH = 224;
const SIDEBAR_COLLAPSED_WIDTH = 60;
const COMPACT_SIDEBAR_COLLAPSED_WIDTH = 42;
const SIDEBAR_WIDTH_STORAGE_KEY = 'app-sidebar-width';
const COMPACT_SIDEBAR_WIDTH_STORAGE_KEY = 'app-sidebar-width-compact';

const SIDEBAR_SIZE_CONFIG = {
  regular: {
    defaultWidth: DEFAULT_SIDEBAR_WIDTH,
    minWidth: MIN_SIDEBAR_WIDTH,
    maxWidth: MAX_SIDEBAR_WIDTH,
    collapsedWidth: SIDEBAR_COLLAPSED_WIDTH,
    storageKey: SIDEBAR_WIDTH_STORAGE_KEY,
    expandedLogoHeight: 30,
    collapsedLogoBaseHeight: 22,
    menuToggleSize: 18,
    menuToggleMarginLeft: 10,
    headerPadding: '0 20px',
    menuIconSize: 20,
  },
  compact: {
    defaultWidth: COMPACT_SIDEBAR_WIDTH,
    minWidth: COMPACT_MIN_SIDEBAR_WIDTH,
    maxWidth: COMPACT_MAX_SIDEBAR_WIDTH,
    collapsedWidth: COMPACT_SIDEBAR_COLLAPSED_WIDTH,
    storageKey: COMPACT_SIDEBAR_WIDTH_STORAGE_KEY,
    expandedLogoHeight: 21,
    collapsedLogoBaseHeight: 18,
    menuToggleSize: 13,
    menuToggleMarginLeft: 6,
    headerPadding: '0 15px',
    menuIconSize: 14,
  },
} as const;

const SHOW_PROMPT_LIBRARY: boolean = false;
const GRAMMAR_OF_USE_MENU_PATH = '/grammar-of-use';
const GRAMMAR_OF_USE_MICROSITE_PATH = 'grammar-of-use-v1.html';
const GRAMMAR_OF_USE_PPS_FRAME_PATH = new URL(
  './pages/AngelInsider/grammar-of-use-v1-PPS.html',
  import.meta.url,
).toString();

const getInsiderMicrositeFileUrl = (micrositeUrl: string | undefined, path: string) => {
  const baseUrl = (micrositeUrl ?? '').replace(/\/+$/, '');
  return baseUrl ? `${baseUrl}/${path}` : '';
};

const getGrammarOfUseFrameUrl = (micrositeUrl: string | undefined) =>
  getInsiderMicrositeFileUrl(micrositeUrl, GRAMMAR_OF_USE_MICROSITE_PATH) ||
  GRAMMAR_OF_USE_PPS_FRAME_PATH;

/**
 * Loads the Grammar of Use copy map for a locale, or null when there is
 * nothing to swap (English is the source language, and an unshipped locale
 * should render the original rather than fail).
 */
const loadGrammarOfUseCopy = async (locale: string): Promise<CopyMap | null> => {
  if (!locale || locale === 'en-US') return null;
  try {
    const mod = await import(`./pages/AngelInsider/data/grammarOfUse/${locale}.json`);
    return (mod.default ?? mod) as CopyMap;
  } catch {
    return null;
  }
};

const ScrollableMenuWrapper = ({
  children,
  resizeHandle,
}: {
  children: React.ReactNode;
  resizeHandle?: React.ReactNode;
}) => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showUpScrollButton, setShowUpScrollButton] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkScroll = () => {
      const isScrollable = el.scrollHeight > el.clientHeight;
      const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
      const isAtTop = el.scrollTop < 20;
      setShowScrollButton(isScrollable && !isAtBottom);
      setShowUpScrollButton(isScrollable && !isAtTop);
    };

    const observer = new MutationObserver(checkScroll);
    observer.observe(el, { childList: true, subtree: true });

    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    // Initial check
    setTimeout(checkScroll, 100);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {showUpScrollButton && (
        <button
          type="button"
          aria-label="Scroll sidebar menu to top"
          onClick={() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
              // Fallback for immediate response if smooth scroll is interrupted or ignored
              // scrollRef.current.scrollTop = 0;
            }
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '18px',
            backgroundColor: Colors.cardBackgroundColor,
            border: 0,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1000,
            opacity: 0.14,
          }}
        >
          <CaretUpFilled style={{ color: Colors.secondaryColor }} />
        </button>
      )}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'none',
          minHeight: 0, // Ensure flex scrolling works correctly
        }}
        className="scrollable-menu-container"
      >
        {children}
      </div>
      {resizeHandle}
      {showScrollButton && (
        <button
          type="button"
          aria-label="Scroll sidebar menu to bottom"
          onClick={() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth',
              });
            }
          }}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '18px',
            backgroundColor: Colors.cardBackgroundColor,
            border: 0,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1000,
            opacity: 0.14,
          }}
        >
          <CaretDownFilled style={{ color: Colors.secondaryColor }} />
        </button>
      )}
    </div>
  );
};

export async function getInitialState(): Promise<{
  fetchUserInfo: () => Promise<API.CurrentUser | undefined>;
  currentUser: API.CurrentUser | undefined;
  userMeta?: API.UserMeta;
  loading?: boolean | undefined;
  settings: Partial<LayoutSettings>;
}> {
  const { pathname } = history.location;

  const fetchUserInfo = async () => {
    try {
      const msg = await getCurrentUser();
      // Only seed the locale from the account's stored preference when the
      // user hasn't made a local choice yet (e.g. via the floating language
      // switcher) - otherwise this runs on every load/reload and immediately
      // stomps a manual switch back to whatever the account profile says,
      // which also re-triggers setLocale's own reload-on-change behavior.
      if (!window.localStorage.getItem('umi_locale')) {
        setLocale(msg.data.locale);
      }
      return msg.data;
    } catch (error) {
      const pathname = history.location.pathname;
      const isPublicAuthPath =
        pathname === loginPath || pathname === resetPasswordPath || pathname === forgotPasswordPath;
      if (!isPublicAuthPath && !isOutlookPath(pathname)) history.push(loginPath);
    }
    return undefined;
  };

  const currentUser = await fetchUserInfo();
  let userMeta: API.UserMeta | undefined;
  if (currentUser) {
    try {
      const { data } = await getUserMeta();
      userMeta = data;
    } catch (error) {
      console.error('Failed to fetch user meta', error);
    }
  }
  if (currentUser && pathname === loginPath) history.push('/');

  return {
    fetchUserInfo,
    currentUser,
    userMeta,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// Sidebar section-divider labels (config/routes.ts `title` field on `isDivider`
// routes) are plain data, not translated by umi's `menu.<name>` mechanism -
// map each divider's stable `path` to an i18n id so menuItemRender can translate them.
const DIVIDER_TITLE_IDS: Record<string, { id: string; defaultMessage: string }> = {
  __divider__1: { id: 'menu.section.vault', defaultMessage: 'Vault' },
  __divider__2: { id: 'menu.section.chatAndCompose', defaultMessage: 'Chat & Compose' },
  __divider__3: { id: 'menu.section.specialised', defaultMessage: 'Specialised' },
  __divider__4: { id: 'menu.section.system', defaultMessage: 'System' },
};

const isSectionDivider = (item: MenuDataItem) => Boolean(item.isDivider && item.path);

const MENU_ICON_RENDERERS: Record<string, (size: number) => React.ReactNode> = {
  HomeIcon: (size) => <HomeIcon size={size} color="#FEFEFE" />,
  FolderIcon: (size) => <FolderIcon size={size} color="#FEFEFE" />,
  SearchIcon: (size) => <SearchIcon size={size} color="#FEFEFE" />,
  LibraryIcon: (size) => <LibraryIcon size={size} color="#FEFEFE" />,
  ChatIcon: (size) => <ChatIcon size={size} color="#FEFEFE" />,
  QuoteIcon: (size) => <QuoteIcon size={size} color="#FEFEFE" />,
  AnalysisIcon: (size) => <AnalysisIcon size={size} color="#FEFEFE" />,
  TemplateIcon: (size) => <TemplateIcon size={size} color="#FEFEFE" />,
  ReviewIcon: (size) => <ReviewIcon size={size} color="#FEFEFE" />,
  SideBySideIcon: (size) => <SideBySideIcon size={size} color="#FEFEFE" />,
  ComparisonIcon: (size) => <ComparisonIcon size={size} color="#FEFEFE" />,
  TeamIcon: (size) => <TeamIcon size={size} color="#FEFEFE" />,
  ProjectsHubIcon: (size) => <ProjectsHubIcon size={size} color="#FEFEFE" />,
  ScribeIcon: (size) => <ScribeIcon size={size} color="#FEFEFE" />,
  WorkstudioIcon: (size) => <WorkstudioIcon size={size} color="#FEFEFE" />,
  TranslationIcon: (size) => <TranslationIcon size={size} color="#FEFEFE" />,
  SettingIcon: (size) => <SettingIcon size={size} color="#FEFEFE" />,
  DiagnosticsIcon: (size) => <DiagnosticsIcon size={size} color="#FEFEFE" />,
  ManagementIcon: (size) => <ManagementIcon size={size} color="#FEFEFE" />,
  AdminIcon: (size) => <AdminIcon size={size} color="#FEFEFE" />,
  GrammarOfUseIcon: (size) => <GrammarOfUseIcon size={size} />,
  HeartIcon: (size) => <HeartIcon size={size} color="#FEFEFE" />,
  AudioIcon: (size) => <AudioIcon size={size} color="#FEFEFE" />,
  ReadIcon: (size) => <ReadIcon size={size} color="#FEFEFE" />,
  DiffIcon: (size) => <DiffIcon size={size} color="#FEFEFE" />,
  ReadOutlined: (size) => <ReadOutlined style={{ fontSize: size }} />,
};

const getDividerTitle = (item: MenuDataItem, intl: ReturnType<typeof getIntl>) => {
  if (!item.isDivider || !item.path) return item.title;

  const dividerTitle = DIVIDER_TITLE_IDS[item.path];
  return dividerTitle ? intl.formatMessage(dividerTitle) : item.title;
};

const getMenuIcon = (icon: MenuDataItem['icon'], size: number) =>
  typeof icon === 'string' ? MENU_ICON_RENDERERS[icon]?.(size) : undefined;

const buildMenuDataItem = (
  item: MenuDataItem,
  intl: ReturnType<typeof getIntl>,
  iconSize: number,
) => {
  const dividerTitle = getDividerTitle(item, intl);
  const icon = getMenuIcon(item.icon, iconSize);

  return {
    ...item,
    ...(item.isDivider && {
      disabled: false,
      disabledTooltip: true,
      name: dividerTitle,
      title: dividerTitle,
    }),
    ...(icon && { icon }),
  };
};

const LayoutChildren = ({
  children,
  initialState,
  intl,
  isMobile,
  location,
  hasGrammarOfUseMicrosite,
  grammarOfUseModalOpen,
  grammarOfUseFrameSrc,
  onCloseGrammarOfUseModal,
}: {
  children: React.ReactNode;
  initialState: any;
  intl: ReturnType<typeof getIntl>;
  isMobile: boolean;
  location: ReturnType<typeof useLocation>;
  hasGrammarOfUseMicrosite: boolean;
  grammarOfUseModalOpen: boolean;
  grammarOfUseFrameSrc: string;
  onCloseGrammarOfUseModal: () => void;
}) => {
  const [clipboard, setClipboard] = useState('');
  const [clipboardOpen, setClipboardOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [prompts, setPrompts] = useState<any>([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);

  useEffect(() => {
    if (libraryOpen && prompts.length === 0) {
      const fetchPrompts = async () => {
        setLoadingPrompts(true);
        try {
          const result = await getLibrary();
          setPrompts(result ?? []);
        } catch (error) {
          console.error('Failed to fetch prompts:', error);
        } finally {
          setLoadingPrompts(false);
        }
      };
      fetchPrompts();
    }
  }, [libraryOpen, prompts.length]);

  const containGrammarOfUseScroll = useCallback(
    (event: React.SyntheticEvent<HTMLIFrameElement>) => {
      try {
        const doc = event.currentTarget.contentDocument;
        if (!doc) return;

        const style = doc.createElement('style');
        style.id = 'grammar-of-use-scroll-containment';
        style.textContent = `
        html,
        body {
          overscroll-behavior: none;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        html::-webkit-scrollbar,
        body::-webkit-scrollbar {
          display: none;
        }

        body {
          overflow-anchor: none;
        }
      `;
        if (!doc.getElementById('grammar-of-use-scroll-containment')) {
          doc.head?.appendChild(style);
        }

        const scrollRoot = doc.scrollingElement ?? doc.documentElement;
        const contentTopOffset =
          Number.parseFloat(
            getComputedStyle(event.currentTarget).getPropertyValue(
              '--grammar-of-use-content-top-offset',
            ),
          ) || 0;

        if (contentTopOffset > 0) {
          scrollRoot.scrollTop = Math.max(scrollRoot.scrollTop, contentTopOffset);

          const keepContentAligned = () => {
            if (scrollRoot.scrollTop < contentTopOffset) {
              scrollRoot.scrollTop = contentTopOffset;
            }
          };

          doc.addEventListener('scroll', keepContentAligned, { passive: true });
        }
      } catch (error) {
        console.warn('Unable to contain Grammar of Use iframe scroll', error);
      }
    },
    [],
  );

  // Disposer for the in-flight localisation observer, so switching documents
  // or closing the modal cannot leave one running against a detached frame.
  const grammarOfUseCopyCleanup = useRef<(() => void) | null>(null);

  const handleGrammarOfUseLoad = useCallback(
    (event: React.SyntheticEvent<HTMLIFrameElement>) => {
      if (hasGrammarOfUseMicrosite) containGrammarOfUseScroll(event);

      grammarOfUseCopyCleanup.current?.();
      grammarOfUseCopyCleanup.current = null;

      const frame = event.currentTarget;
      // The remote microsite is cross-origin, so only the bundled fallback can
      // be localised here; loadGrammarOfUseCopy resolves to null for en-US.
      void loadGrammarOfUseCopy(getLocale()).then((copy) => {
        if (!copy || !frame.isConnected) return;
        grammarOfUseCopyCleanup.current = localizeGrammarOfUseFrame(frame, copy);
      });
    },
    [containGrammarOfUseScroll, hasGrammarOfUseMicrosite],
  );

  useEffect(
    () => () => {
      grammarOfUseCopyCleanup.current?.();
      grammarOfUseCopyCleanup.current = null;
    },
    [],
  );

  const copyPromptContent = (content: string) => {
    navigator.clipboard.writeText(content);
    message.success(
      intl.formatMessage({
        id: 'components.appShell.promptLibrary.copiedMessage',
        defaultMessage: 'Prompt copied to clipboard!',
      }),
    );
  };

  if (initialState?.loading) return <PageLoading />;

  return (
    <DownloadLoadingProvider>
      <FileTreeProvider>
        <ConfigProvider
          theme={{
            token: {
              fontFamily: 'Inter, sans-serif',
              colorTextLabel: Colors.primaryColor,
              colorTextHeading: Colors.secondaryColor,
              colorPrimary: Colors.primaryColor,
              colorLink: Colors.primaryColor,
              colorError: Colors.errorColor,
              colorErrorText: Colors.errorColor,
              // borderRadius: 20,
              // borderRadiusSM: 20,
              // borderRadiusLG: 20,
              colorText: Colors.secondaryColor,
              colorTextBase: Colors.secondaryColor,
              colorBgSpotlight: Colors.primaryColor,
            },
            components: {
              Modal: {
                titleColor: Colors.primaryColor,
              },
              Button: {
                fontFamily: 'Optima, Segoe, Segoe UI, Candara, Calibri, Arial, sans-serif',
              },
            },
          }}
        >
          <BillingRequiredOverlay
            hidden={!initialState?.currentUser || location.pathname.startsWith('/management')}
            canManageBilling={
              initialState?.currentUser?.role === 'admin' ||
              initialState?.currentUser?.isSuperAdmin === true
            }
            onManageBilling={() => history.push('/management')}
          >
            <FileTreeInitializer currentUser={initialState?.currentUser}>
              <IdleForm />
              {children}
            </FileTreeInitializer>
          </BillingRequiredOverlay>
          <CleanTextContextMenu />
          <Modal
            className={`grammar-of-use-modal${
              hasGrammarOfUseMicrosite ? '' : ' grammar-of-use-modal-pps'
            }`}
            wrapClassName="grammar-of-use-modal-wrap"
            open={grammarOfUseModalOpen}
            onCancel={onCloseGrammarOfUseModal}
            footer={null}
            width="var(--grammar-of-use-modal-width)"
            title={null}
            style={{ top: 'var(--grammar-of-use-modal-top)', paddingBottom: 0 }}
            destroyOnClose
            closeIcon={null}
            styles={{
              body: { padding: 0, overflow: 'hidden' },
              content: {
                padding: 0,
                overflow: 'hidden',
                background: 'transparent',
                boxShadow: 'none',
              },
            }}
          >
            <div
              className={`grammar-of-use-frame${
                hasGrammarOfUseMicrosite ? '' : ' grammar-of-use-frame-pps'
              }`}
              style={{
                width: 'var(--grammar-of-use-modal-width)',
                height: 'var(--grammar-of-use-modal-height)',
                overflow: 'hidden',
                borderRadius: 8,
              }}
            >
              <iframe
                className={`grammar-of-use-frame-iframe${
                  hasGrammarOfUseMicrosite ? '' : ' grammar-of-use-frame-iframe-pps'
                }`}
                title="Grammar of Use"
                src={grammarOfUseFrameSrc}
                onLoad={handleGrammarOfUseLoad}
                style={{
                  display: 'block',
                  width: 'var(--grammar-of-use-iframe-width)',
                  height: 'var(--grammar-of-use-iframe-height)',
                  marginTop: 'var(--grammar-of-use-iframe-top)',
                  marginLeft: 'var(--grammar-of-use-iframe-left)',
                  border: 0,
                  borderRadius: 8,
                }}
              />
            </div>
          </Modal>
          {!isMobile && (
            <>
              {SHOW_PROMPT_LIBRARY && (
                <>
                  <Card
                    hidden={libraryOpen}
                    size="small"
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: 150,
                      zIndex: 9998,
                      background: Colors.primaryColor,
                      border: 0,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                    styles={{ body: { padding: 0 } }}
                  >
                    <Button
                      type="primary"
                      size="large"
                      icon={<ReadOutlined />}
                      style={{ borderRadius: '10px 0 0 10px' }}
                      onClick={() => setLibraryOpen(true)}
                    />
                  </Card>
                  <Card
                    hidden={!libraryOpen}
                    size="small"
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: 150,
                      borderRadius: '8px 0 0 8px',
                      zIndex: 9998,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                      width: '30%',
                      opacity: 0.8,
                    }}
                    styles={{ body: { padding: '4px 13px 4px 4px' }, header: { border: 0 } }}
                    title={intl.formatMessage({
                      id: 'components.appShell.promptLibrary.title',
                      defaultMessage: 'Prompt Library',
                    })}
                    extra={[
                      <Button
                        key="close-library"
                        type="link"
                        danger
                        icon={<MinusOutlined />}
                        onClick={() => setLibraryOpen(false)}
                      />,
                    ]}
                  >
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <List
                        loading={loadingPrompts}
                        dataSource={prompts.data}
                        locale={{
                          emptyText: intl.formatMessage({
                            id: 'components.appShell.promptLibrary.noPromptsAvailable',
                            defaultMessage: 'No prompts available',
                          }),
                        }}
                        renderItem={(prompt: any) => (
                          <List.Item
                            style={{
                              padding: '8px 0',
                              borderBottom: '1px solid #f0f0f0',
                            }}
                            actions={[
                              <Button
                                key="copy-prompt"
                                type="text"
                                icon={<CopyIcon />}
                                size="small"
                                onClick={() => copyPromptContent(prompt.content)}
                                title={intl.formatMessage({
                                  id: 'components.appShell.promptLibrary.copyTooltip',
                                  defaultMessage: 'Copy prompt content',
                                })}
                              />,
                            ]}
                          >
                            <List.Item.Meta
                              title={
                                <Typography.Text
                                  strong
                                  style={{
                                    fontSize: '12px',
                                    color: Colors.primaryColor,
                                  }}
                                >
                                  {prompt.name ??
                                    intl.formatMessage({
                                      id: 'components.appShell.promptLibrary.untitledPrompt',
                                      defaultMessage: 'Untitled Prompt',
                                    })}
                                </Typography.Text>
                              }
                              description={
                                <Typography.Text
                                  type="secondary"
                                  style={{
                                    fontSize: '11px',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {prompt.purpose}
                                </Typography.Text>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  </Card>
                </>
              )}
              <Card
                hidden={clipboardOpen}
                size="small"
                style={{
                  position: 'absolute',
                  right: 0,
                  bottom: 100,
                  zIndex: 9999,
                  background: Colors.primaryColor,
                  border: 0,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
                styles={{ body: { padding: 0 } }}
              >
                <Button
                  type="primary"
                  size="large"
                  icon={<ClipboardIcon />}
                  style={{ borderRadius: '10px 0 0 10px' }}
                  onClick={() => setClipboardOpen(true)}
                />
              </Card>
              <Card
                hidden={!clipboardOpen}
                size="small"
                style={{
                  position: 'absolute',
                  right: 0,
                  bottom: 100,
                  borderRadius: '8px 0 0 8px',
                  zIndex: 9999,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  width: '40%',
                  opacity: 0.8,
                }}
                styles={{ body: { padding: '4px 13px 4px 4px' }, header: { border: 0 } }}
                title={intl.formatMessage({
                  id: 'components.appShell.clipboard.title',
                  defaultMessage: 'Clipboard',
                })}
                extra={[
                  <Button
                    key="copy-clipboard"
                    type="link"
                    icon={<CopyIcon />}
                    onClick={() => {
                      navigator.clipboard.writeText(clipboard);
                      message.success(
                        intl.formatMessage({
                          id: 'components.appShell.clipboard.copiedMessage',
                          defaultMessage: 'Copied!',
                        }),
                      );
                    }}
                  />,
                  <Button
                    key="close-clipboard"
                    type="link"
                    danger
                    icon={<MinusOutlined />}
                    onClick={() => setClipboardOpen(false)}
                  />,
                ]}
              >
                <Input.TextArea
                  value={clipboard}
                  onChange={(e) => setClipboard(e.target.value)}
                  autoSize={{ minRows: 8, maxRows: 18 }}
                  style={{ borderRadius: 4 }}
                  variant="filled"
                />
              </Card>
            </>
          )}
        </ConfigProvider>
      </FileTreeProvider>
    </DownloadLoadingProvider>
  );
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
const Layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  // Locale switches reload the page anyway, so this current-locale snapshot is
  // equivalent here without needing the useIntl hook.
  const intl = getIntl();
  const smallLogo = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const [collapsed, setCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [grammarOfUseModalOpen, setGrammarOfUseModalOpen] = useState(false);
  const sidebarWidthRef = useRef(DEFAULT_SIDEBAR_WIDTH);
  const sidebarResizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const isCompactSidebar = useCompactSidebar();
  const location = useLocation();
  const sidebarSizing = isCompactSidebar
    ? SIDEBAR_SIZE_CONFIG.compact
    : SIDEBAR_SIZE_CONFIG.regular;
  const expandedLogoHeight = smallLogo ? 12 : sidebarSizing.expandedLogoHeight;
  const collapsedLogoBaseHeight = smallLogo ? 12 : sidebarSizing.collapsedLogoBaseHeight;
  const collapsedLogoMinHeight = isCompactSidebar ? 8 : 9;
  const collapsedLogoHeight = Math.max(
    collapsedLogoMinHeight,
    Math.round(collapsedLogoBaseHeight * 0.5),
  );
  const logoHeight = collapsed ? collapsedLogoHeight : expandedLogoHeight;
  const hasGrammarOfUseMicrosite = Boolean(initialState?.userMeta?.micrositeUrl);
  const grammarOfUseFrameSrc = getGrammarOfUseFrameUrl(initialState?.userMeta?.micrositeUrl);

  useEffect(() => {
    setCollapsed(isMobile);
  }, [isMobile]);

  useEffect(() => {
    sidebarWidthRef.current = sidebarWidth;
  }, [sidebarWidth]);

  useEffect(() => {
    const storedWidth = Number(window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY));
    const activeStoredWidth = Number(window.localStorage.getItem(sidebarSizing.storageKey));
    let nextWidth: number = sidebarSizing.defaultWidth;
    if (Number.isFinite(activeStoredWidth) && activeStoredWidth > 0) {
      nextWidth = activeStoredWidth;
    } else if (!isCompactSidebar && Number.isFinite(storedWidth) && storedWidth > 0) {
      nextWidth = storedWidth;
    }
    setSidebarWidth(Math.min(sidebarSizing.maxWidth, Math.max(sidebarSizing.minWidth, nextWidth)));
  }, [
    isCompactSidebar,
    sidebarSizing.defaultWidth,
    sidebarSizing.maxWidth,
    sidebarSizing.minWidth,
    sidebarSizing.storageKey,
  ]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      if (!sidebarResizeRef.current) return;
      const nextWidth = Math.min(
        sidebarSizing.maxWidth,
        Math.max(
          sidebarSizing.minWidth,
          sidebarResizeRef.current.startWidth + event.clientX - sidebarResizeRef.current.startX,
        ),
      );
      setSidebarWidth(nextWidth);
    };

    const handlePointerUp = () => {
      if (!sidebarResizeRef.current) return;
      sidebarResizeRef.current = null;
      window.localStorage.setItem(sidebarSizing.storageKey, String(sidebarWidthRef.current));
      document.body.classList.remove('app-sidebar-is-resizing');
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      document.body.classList.remove('app-sidebar-is-resizing');
    };
  }, [sidebarSizing.maxWidth, sidebarSizing.minWidth, sidebarSizing.storageKey]);

  const handleSidebarResizePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (collapsed || isMobile) return;
    event.preventDefault();
    event.stopPropagation();
    sidebarResizeRef.current = { startX: event.clientX, startWidth: sidebarWidth };
    document.body.classList.add('app-sidebar-is-resizing');
  };

  const handleLogout = useCallback(async () => {
    flushSync(() => setInitialState((s) => ({ ...s, currentUser: undefined } as any)));
    clearAllWsSessions();
    localStorage.removeItem('tokenRefresh');
    await logout();
    localStorage.removeItem('token');
    history.push('/user/login');
  }, [setInitialState]);

  const toggleSection = (sectionPath?: string) => {
    if (!sectionPath) return;
    setCollapsedSections((sections) => ({
      ...sections,
      [sectionPath]: !sections[sectionPath],
    }));
  };

  // Compute the menu-matching pathname: for wildcard routes like '/review/*',
  // always map to '/review/' so ProLayout can match the correct menu item.
  // This handles both sub-paths (e.g. '/review/quick') and the base path
  // (e.g. '/review' or '/review/') consistently.
  const wildcardBases = [
    'files',
    'chat',
    'transcribe',
    'apps',
    'review',
    'side-by-side',
    'comparison',
    'translation',
  ];
  const menuLocation = (() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const base = segments[0];
    if (base && wildcardBases.includes(base)) {
      return { ...location, pathname: `/${base}/` };
    }
    return location;
  })();
  return {
    actionsRender: () => [],
    avatarProps: {
      render: () => (!isMobile ? <SidebarActionsWrapper collapsed={collapsed} /> : null),
    },
    logo: (
      <img
        src="/logo.svg"
        alt="logo"
        style={{
          height: logoHeight,
          width: 'auto',
          display: 'block',
          transition: 'height 0.2s ease, width 0.2s ease',
        }}
      />
    ),
    pageTitleRender: false,
    footerRender: false,
    onPageChange: () => {
      const { pathname } = history.location;
      if (isOutlookPath(pathname)) return;
      const publicPath =
        pathname === loginPath || pathname === resetPasswordPath || pathname === forgotPasswordPath;
      if (initialState?.currentUser) {
        if (initialState?.currentUser?.forceChangePassword === true) {
          history.push(updatePasswordPath);
        } else if (publicPath) history.push('/welcome');
      } else if (!publicPath) history.push(loginPath);
    },
    collapsed,
    onCollapse: setCollapsed,
    menu: {
      type: 'group',
      collapsedShowTitle: false,
    },
    menuContentRender: (_, dom) => (
      <FilesSidebarResetBoundary>
        <ScrollableMenuWrapper
          resizeHandle={
            !collapsed && !isMobile ? (
              <button
                type="button"
                aria-label="Resize sidebar"
                className="app-sidebar-resize-handle"
                onPointerDown={handleSidebarResizePointerDown}
              >
                <HolderOutlined />
              </button>
            ) : null
          }
        >
          {dom}
        </ScrollableMenuWrapper>
      </FilesSidebarResetBoundary>
    ),
    siderWidth: sidebarWidth,
    collapsedWidth: sidebarSizing.collapsedWidth,
    breakpoint: isMobile ? 'lg' : false,
    collapsedButtonRender: false,
    links: [],
    location: menuLocation,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    menuHeaderRender: (logo, _title) => (
      <div
        style={{
          display: 'flex',
          flexDirection: collapsed && !isMobile ? 'column' : 'row',
          gap: collapsed && !isMobile ? 8 : 0,
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? 0 : sidebarSizing.headerPadding,
          width: '100%',
        }}
      >
        {logo}
        {!isMobile && (
          <Button
            type="text"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setCollapsed(!collapsed);
            }}
            className="sidebar-toggle-button"
            style={{
              marginLeft: collapsed ? 0 : sidebarSizing.menuToggleMarginLeft,
            }}
            icon={<MenuIcon size={sidebarSizing.menuToggleSize} color="currentColor" />}
          />
        )}
      </div>
    ),
    menuItemRender: (item: MenuDataItem, dom) => {
      if (item.isDivider) {
        const sectionTitle =
          item.path && DIVIDER_TITLE_IDS[item.path]
            ? intl.formatMessage(DIVIDER_TITLE_IDS[item.path])
            : item.title;
        const isSectionCollapsed = Boolean(item.path && collapsedSections[item.path]);

        const sectionHeader = (
          <button
            type="button"
            className={`titleFont sidebar-section-header ${
              collapsed ? 'sidebar-section-header-collapsed' : ''
            }`}
            aria-expanded={!isSectionCollapsed}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleSection(item.path);
            }}
          >
            <span>{sectionTitle}</span>
            {isSectionCollapsed ? (
              <CaretDownFilled className="sidebar-section-header-caret" />
            ) : (
              <CaretUpFilled className="sidebar-section-header-caret" />
            )}
          </button>
        );

        return collapsed ? (
          <Tooltip title={sectionTitle} placement="right">
            {sectionHeader}
          </Tooltip>
        ) : (
          sectionHeader
        );
      }

      if (item.key === 'logout') {
        return (
          <Button
            type="text"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleLogout();
            }}
            className="menu-tall"
            style={{
              cursor: 'pointer',
              height: 'auto',
              padding: 0,
              textAlign: 'left',
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>{dom}</span>
            </div>
          </Button>
        );
      }

      if (!item.path) return dom;
      if (item.path === GRAMMAR_OF_USE_MENU_PATH) {
        return (
          <Link
            to={GRAMMAR_OF_USE_MENU_PATH}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setGrammarOfUseModalOpen(true);
            }}
            className="menu-tall"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>{dom}</span>
            </div>
          </Link>
        );
      }

      // Strip trailing /* from wildcard routes so menu links navigate to the base path
      const linkPath = item.path.replace(/\/\*$/, '') || '/';
      return (
        <Link to={linkPath} className="menu-tall">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>{dom}</span>
            {item.isBeta && !collapsed && (
              <sup style={{ fontSize: 10, color: '#fff', fontWeight: 700 }}>Beta</sup>
            )}
          </div>
        </Link>
      );
    },
    postMenuData: (menuData) => {
      const processedData =
        menuData?.map((item) => buildMenuDataItem(item, intl, sidebarSizing.menuIconSize)) || [];

      let activeSectionPath: string | undefined;
      const visibleMenuData = processedData.filter((item) => {
        if (isSectionDivider(item)) {
          activeSectionPath = item.path;
          return true;
        }
        if (activeSectionPath && collapsedSections[activeSectionPath]) {
          return false;
        }
        return true;
      });

      if (isMobile) {
        visibleMenuData.push({
          path: '/user/login',
          name: intl.formatMessage({
            id: 'components.appShell.logoutMenuItem',
            defaultMessage: 'Logout',
          }),
          icon: <LogoutIcon size={20} />,
          key: 'logout',
        });
      }
      return visibleMenuData;
    },
    unAccessible: (
      <ConfigProvider
        theme={{
          token: {
            borderRadius: 20,
            colorText: Colors.secondaryColor,
            colorTextBase: Colors.secondaryColor,
          },
        }}
      >
        <PageContainer pageHeaderRender={false}>
          <ProCard style={{ height: '100dvh' }}>
            <Result
              icon={<img alt="Chatroom" src="/403.svg" style={{ height: '60vh' }} />}
              title={intl.formatMessage({
                id: 'components.appShell.unauthorized.title',
                defaultMessage: 'This page is not meant for you',
              })}
              extra={
                <Button type="primary" onClick={() => history.push('/')}>
                  <FormattedMessage
                    id="components.appShell.unauthorized.backHome"
                    defaultMessage="Back Home"
                  />
                </Button>
              }
            />
          </ProCard>
        </PageContainer>
      </ConfigProvider>
    ),
    // 增加一个 loading 的状态
    childrenRender: (children) => (
      <LayoutChildren
        initialState={initialState}
        intl={intl}
        isMobile={isMobile}
        location={location}
        hasGrammarOfUseMicrosite={hasGrammarOfUseMicrosite}
        grammarOfUseModalOpen={grammarOfUseModalOpen}
        grammarOfUseFrameSrc={grammarOfUseFrameSrc}
        onCloseGrammarOfUseModal={() => setGrammarOfUseModalOpen(false)}
      >
        {children}
      </LayoutChildren>
    ),
    token: {
      // 参见ts声明，demo 见文档，通过token 修改样式
      //https://procomponents.ant.design/components/layout#%E9%80%9A%E8%BF%87-token-%E4%BF%AE%E6%94%B9%E6%A0%B7%E5%BC%8F
      colorPrimary: Colors.primaryColor,
      header: {
        colorBgHeader: Colors.secondaryColor,
        colorHeaderTitle: Colors.headerColor,
      },
      sider: {
        colorMenuBackground: Colors.backgroundColor,
        colorTextMenu: '#929CAC',
      },
    },
    ...initialState?.settings,
  };
};

export { Layout as layout };

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};
